import { useQuery, useMutation } from "@tanstack/react-query";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

type Notification = {
  id: string;
  type: string;
  title: string;
  message: string | null;
  link: string | null;
  read: boolean;
  createdAt: string;
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const { data: notifications } = useQuery<Notification[]>({
    queryKey: ["/api/me/notifications"],
    refetchInterval: 5000,
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("PATCH", `/api/me/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/me/notifications"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/me/notifications/read-all`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/me/notifications"] });
    },
  });

  const clearAll = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/me/notifications`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/me/notifications"] });
    },
  });

  const unread = notifications?.filter(n => !n.read) ?? [];
  const unreadCount = unread.length;

  useEffect(() => {
    if (!notifications) return;
    // On the first load, record every existing notification as already-seen so
    // we don't toast for pre-existing items.
    if (!initializedRef.current) {
      initializedRef.current = true;
      for (const n of notifications) seenIdsRef.current.add(n.id);
      return;
    }
    // A "new" notification is one whose id we've never seen in a poll. Tracking
    // seen ids (instead of comparing the top unread id) avoids false toasts when
    // the unread list merely shrinks/reorders because an item was marked read.
    const fresh = notifications.find(n => !n.read && !seenIdsRef.current.has(n.id));
    for (const n of notifications) seenIdsRef.current.add(n.id);
    if (fresh) {
      toast({
        title: fresh.title,
        description: fresh.message || undefined,
        action: fresh.link ? (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              markRead.mutate(fresh.id);
              navigate(fresh.link!);
            }}
            data-testid="button-notification-toast-view"
          >
            View
          </Button>
        ) : undefined,
      });
    }
  }, [notifications]);

  if (!notifications) return null;

  const handleClick = (n: Notification) => {
    if (!n.read) markRead.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8" data-testid="button-notification-bell">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center" data-testid="badge-notification-count">
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="panel-notifications">
        <div className="px-3 py-2 border-b border-border/40 flex items-center justify-between gap-1">
          <p className="text-sm font-semibold">Notifications</p>
          <div className="flex items-center gap-0.5">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs gap-1"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                data-testid="button-mark-all-read"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all as read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs gap-1 text-muted-foreground"
                onClick={() => clearAll.mutate()}
                disabled={clearAll.isPending}
                data-testid="button-clear-all-notifications"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Clear all
              </Button>
            )}
          </div>
        </div>
        {notifications.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            No notifications
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-border/30">
            {notifications.map(n => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={cn(
                  "w-full text-left px-3 py-2.5 transition-colors flex gap-2",
                  n.read ? "hover:bg-muted/50" : "bg-primary/5 hover:bg-primary/10",
                )}
                data-testid={`notification-item-${n.id}`}
              >
                <span
                  className={cn(
                    "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                    n.read ? "bg-transparent" : "bg-primary",
                  )}
                  data-testid={n.read ? undefined : `dot-notification-unread-${n.id}`}
                  aria-hidden={n.read}
                />
                <div className="min-w-0 flex-1">
                  <p className={cn("text-sm leading-tight", n.read ? "font-medium" : "font-semibold")}>{n.title}</p>
                  {n.message && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground/60 mt-1">
                    {new Date(n.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
